import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
  FaPrint,
  FaTable,
} from "react-icons/fa";

import { fetchSyncRecords } from "./fieldAppApi";
import FieldAppPlantingCalibrationSpreadsheet, {
  FieldAppPlantingCalibrationAverages,
} from "./FieldAppPlantingCalibrationSpreadsheet";
import {
  CALIBRATION_CHECK_EVENT_TYPE,
  calibrationCheckFromSyncRecord,
  summarizeCalibrationChecks,
} from "./calibrationCheckOrderMetrics";
import {
  buildPlantingCalibrationSpreadsheetCsvRows,
  buildPlantingCalibrationSpreadsheetFileName,
  downloadPlantingCalibrationSpreadsheetCsv,
} from "./plantingCalibrationSpreadsheetExport";
import {
  exportTelemetrySchedulePdf,
  printTelemetrySchedule,
} from "./fieldAppTelemetryScheduleExport";

import "./FieldAppHarvestLossOrderResults.css";
import "./FieldAppPlantingCalibrationOrderResults.css";

export default function FieldAppPlantingCalibrationOrderResults({
  farmId,
  observationId,
  orderLabel,
  selection,
  variant = "planting",
  defaultExpanded = false,
  t,
}) {
  const isFertilization = variant === "fertilization";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [exportingPdf, setExportingPdf] = useState(false);
  const printAreaRef = useRef(null);

  useEffect(() => {
    if (defaultExpanded) {
      setExpanded(true);
    }
  }, [defaultExpanded, observationId]);

  useEffect(() => {
    if (!farmId || !observationId) return undefined;

    let cancelled = false;

    async function loadChecks() {
      setLoading(true);
      try {
        const response = await fetchSyncRecords(
          farmId,
          { event_type: CALIBRATION_CHECK_EVENT_TYPE, limit: 200 },
          selection
        );
        if (cancelled) return;

        const parsed = (response.results || response || [])
          .map(calibrationCheckFromSyncRecord)
          .filter(Boolean)
          .filter(
            (row) =>
              row.serviceOrderId != null &&
              Number(row.serviceOrderId) === Number(observationId)
          )
          .sort((a, b) => {
            const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return ta - tb;
          });

        setRows(parsed);
      } catch (_) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadChecks();
    return () => {
      cancelled = true;
    };
  }, [farmId, observationId, selection]);

  const summary = useMemo(() => summarizeCalibrationChecks(rows), [rows]);

  const resolvedOrderLabel =
    orderLabel || (observationId ? `#${observationId}` : "");

  const exportFileName = useMemo(
    () =>
      buildPlantingCalibrationSpreadsheetFileName({
        observationId,
        orderLabel: resolvedOrderLabel,
      }),
    [observationId, resolvedOrderLabel]
  );

  const handleExportCsv = useCallback(() => {
    const csvRows = buildPlantingCalibrationSpreadsheetCsvRows({
      rows,
      t,
    });
    downloadPlantingCalibrationSpreadsheetCsv(csvRows, exportFileName);
  }, [rows, exportFileName, t]);

  const ensureExpandedForExport = useCallback(async () => {
    if (expanded) return;
    setExpanded(true);
    await new Promise((resolve) => window.setTimeout(resolve, 120));
  }, [expanded]);

  const handlePrint = useCallback(async () => {
    await ensureExpandedForExport();
    printTelemetrySchedule();
  }, [ensureExpandedForExport]);

  const handleExportPdf = useCallback(async () => {
    if (exportingPdf) return;
    await ensureExpandedForExport();
    if (!printAreaRef.current) return;
    setExportingPdf(true);
    try {
      await exportTelemetrySchedulePdf(
        printAreaRef.current,
        exportFileName.replace(/\.csv$/i, ".pdf")
      );
    } finally {
      setExportingPdf(false);
    }
  }, [ensureExpandedForExport, exportFileName, exportingPdf]);

  const hasRows = rows.length > 0;
  const canExport = hasRows && !loading;

  return (
    <div className="field-app-harvest-loss-order-results field-app-planting-calibration-order-results">
      <div className="field-app-harvest-loss-panel field-app-planting-calibration-panel">
        <div className="field-app-harvest-loss-panel__header">
          <button
            type="button"
            className="field-app-harvest-loss-panel__toggle field-app-planting-calibration-panel__toggle btn btn-default"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            <FaTable aria-hidden="true" />
            <span>
              {t(
                isFertilization
                  ? "Planilha de fertilização"
                  : "Planilha de plantio"
              )}
            </span>
            {hasRows ? (
              <span className="field-app-harvest-loss-panel__count">
                {rows.length} {t("lançamento(s)")}
              </span>
            ) : null}
            {expanded ? (
              <FaChevronUp aria-hidden="true" />
            ) : (
              <FaChevronDown aria-hidden="true" />
            )}
          </button>

          {canExport ? (
            <div className="field-app-harvest-loss-panel__toolbar">
              <button
                type="button"
                className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                onClick={handleExportCsv}
                aria-label={t("Exportar planilha CSV (Excel)")}
                title={t("Exportar planilha CSV (Excel)")}
              >
                <FaFileDownload aria-hidden="true" />
                <span className="field-app-harvest-loss-panel__toolbar-label">
                  CSV
                </span>
              </button>
              <button
                type="button"
                className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                onClick={handlePrint}
                aria-label={t("Imprimir planilha")}
                title={t("Imprimir planilha")}
              >
                <FaPrint aria-hidden="true" />
              </button>
              <button
                type="button"
                className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                onClick={handleExportPdf}
                disabled={exportingPdf}
                aria-label={t("Salvar planilha em PDF")}
                title={
                  exportingPdf ? t("Gerando PDF…") : t("Salvar planilha em PDF")
                }
              >
                <FaFileDownload aria-hidden="true" />
                <span className="field-app-harvest-loss-panel__toolbar-label">
                  PDF
                </span>
              </button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <p className="field-app-harvest-loss-loading">
            {t("Carregando lançamentos...")}
          </p>
        ) : null}

        {!loading && !hasRows ? (
          <p className="field-app-harvest-loss-empty-note">
            {t(
              isFertilization
                ? "Nenhum check de calibragem de fertilização nesta ordem ainda. Os lançamentos do app aparecem aqui."
                : "Nenhum check de calibragem de plantio nesta ordem ainda. Os lançamentos do app aparecem aqui."
            )}
          </p>
        ) : null}

        {!loading && hasRows && !expanded ? (
          <div className="field-app-harvest-loss-collapsed field-app-harvest-loss-spreadsheet field-app-planting-calibration-spreadsheet">
            <FieldAppPlantingCalibrationAverages
              summary={summary}
              variant={variant}
              t={t}
            />
            <button
              type="button"
              className="field-app-harvest-loss-expand-btn"
              onClick={() => setExpanded(true)}
            >
              {t("Expandir planilha completa ({{count}} lançamentos)", {
                count: rows.length,
              })}
            </button>
          </div>
        ) : null}

        {!loading && hasRows && expanded ? (
          <div className="field-app-harvest-loss-panel__body">
            <p className="field-app-telemetry-schedule__hint field-app-harvest-loss-panel__hint">
              {t(
                "Todos os checks de calibragem enviados pelo app nesta ordem. Use o download para abrir no Excel."
              )}
            </p>
            <div
              ref={printAreaRef}
              className="field-app-harvest-loss-spreadsheet__print-area"
            >
              <FieldAppPlantingCalibrationSpreadsheet
                rows={rows}
                summary={summary}
                orderLabel={resolvedOrderLabel}
                variant={variant}
                t={t}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
